import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, query, orderBy, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface SalaryPaymentProps {
  domain: string;
}

interface Teacher {
  id: string;
  employeeId: string;
  name: string;
  designation: string;
  department: string;
  joiningDate: string;
  salary: number;
  accountNumber?: string;
  bankName?: string;
  ifscCode?: string;
}

interface SalaryPayment {
  id: string;
  teacherId: string;
  teacherName: string;
  employeeId: string;
  month: string;
  year: number;
  basicSalary: number;
  allowances: number;
  deductions: number;
  netSalary: number;
  paymentDate: string;
  paymentMode: string;
  transactionId?: string;
  status: 'pending' | 'paid' | 'failed';
  remarks?: string;
  createdAt: string;
}

const SalaryPayment: React.FC<SalaryPaymentProps> = ({ domain }) => {
  const [activeView, setActiveView] = useState<'list' | 'create'>('list');
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [payments, setPayments] = useState<SalaryPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const [formData, setFormData] = useState({
    teacherId: '',
    month: '',
    year: new Date().getFullYear(),
    basicSalary: 0,
    allowances: 0,
    deductions: 0,
    paymentMode: 'bank_transfer',
    transactionId: '',
    remarks: ''
  });

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    fetchTeachers();
    fetchPayments();
  }, [domain]);

  const fetchTeachers = async () => {
    try {
      const teachersRef = collection(db, `school-users/${domain}/teachers`);
      const snapshot = await getDocs(teachersRef);
      const teacherList: Teacher[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Teacher));
      setTeachers(teacherList);
    } catch (error: any) {
      console.error('Error fetching teachers:', error);
    }
  };

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const paymentsRef = collection(db, `school-users/${domain}/salary-payments`);
      const q = query(paymentsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const paymentList: SalaryPayment[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as SalaryPayment));
      setPayments(paymentList);
    } catch (error: any) {
      console.error('Error fetching payments:', error);
      alert(`Failed to load payments: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTeacherSelect = (teacherId: string) => {
    const teacher = teachers.find(t => t.id === teacherId);
    if (teacher) {
      setFormData({
        ...formData,
        teacherId,
        basicSalary: teacher.salary || 0
      });
    }
  };

  const calculateNetSalary = () => {
    return formData.basicSalary + formData.allowances - formData.deductions;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.teacherId || !formData.month) {
      alert('Please select teacher and month');
      return;
    }

    // Check if payment already exists for this month/year
    const existingPayment = payments.find(
      p => p.teacherId === formData.teacherId &&
           p.month === formData.month &&
           p.year === formData.year
    );

    if (existingPayment) {
      alert('Payment already exists for this teacher for the selected month/year');
      return;
    }

    setLoading(true);
    try {
      const teacher = teachers.find(t => t.id === formData.teacherId);
      const netSalary = calculateNetSalary();

      const paymentData: Omit<SalaryPayment, 'id'> = {
        teacherId: formData.teacherId,
        teacherName: teacher?.name || '',
        employeeId: teacher?.employeeId || '',
        month: formData.month,
        year: formData.year,
        basicSalary: formData.basicSalary,
        allowances: formData.allowances,
        deductions: formData.deductions,
        netSalary,
        paymentDate: new Date().toISOString(),
        paymentMode: formData.paymentMode,
        transactionId: formData.transactionId,
        status: 'paid',
        remarks: formData.remarks,
        createdAt: new Date().toISOString()
      };

      await addDoc(collection(db, `school-users/${domain}/salary-payments`), paymentData);

      alert('Salary payment recorded successfully!');
      setFormData({
        teacherId: '',
        month: '',
        year: new Date().getFullYear(),
        basicSalary: 0,
        allowances: 0,
        deductions: 0,
        paymentMode: 'bank_transfer',
        transactionId: '',
        remarks: ''
      });
      setActiveView('list');
      fetchPayments();
    } catch (error: any) {
      console.error('Error recording payment:', error);
      alert(`Failed to record payment: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (paymentId: string, newStatus: 'pending' | 'paid' | 'failed') => {
    try {
      const paymentRef = doc(db, `school-users/${domain}/salary-payments`, paymentId);
      await updateDoc(paymentRef, { status: newStatus });
      alert('Payment status updated successfully!');
      fetchPayments();
    } catch (error: any) {
      console.error('Error updating status:', error);
      alert(`Failed to update status: ${error.message}`);
    }
  };

  const filteredPayments = payments.filter(payment => {
    let matches = true;

    if (selectedMonth && payment.month !== selectedMonth) {
      matches = false;
    }

    if (selectedYear && payment.year !== selectedYear) {
      matches = false;
    }

    if (filterStatus !== 'all' && payment.status !== filterStatus) {
      matches = false;
    }

    return matches;
  });

  const totalPaid = filteredPayments
    .filter(p => p.status === 'paid')
    .reduce((sum, p) => sum + p.netSalary, 0);

  const totalPending = filteredPayments
    .filter(p => p.status === 'pending')
    .reduce((sum, p) => sum + p.netSalary, 0);

  return (
    <div>
      {/* Summary Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="rounded-circle bg-success bg-opacity-10 p-3 me-3">
                  <i className="bi bi-check-circle-fill text-success fs-4"></i>
                </div>
                <div>
                  <small className="text-muted d-block">Total Paid</small>
                  <h5 className="mb-0">₹{totalPaid.toLocaleString('en-IN')}</h5>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="rounded-circle bg-warning bg-opacity-10 p-3 me-3">
                  <i className="bi bi-clock-fill text-warning fs-4"></i>
                </div>
                <div>
                  <small className="text-muted d-block">Total Pending</small>
                  <h5 className="mb-0">₹{totalPending.toLocaleString('en-IN')}</h5>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="rounded-circle bg-primary bg-opacity-10 p-3 me-3">
                  <i className="bi bi-people-fill text-primary fs-4"></i>
                </div>
                <div>
                  <small className="text-muted d-block">Total Teachers</small>
                  <h5 className="mb-0">{teachers.length}</h5>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="rounded-circle bg-info bg-opacity-10 p-3 me-3">
                  <i className="bi bi-receipt-cutoff text-info fs-4"></i>
                </div>
                <div>
                  <small className="text-muted d-block">Total Payments</small>
                  <h5 className="mb-0">{filteredPayments.length}</h5>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {activeView === 'list' ? (
        <div className="card shadow-sm">
          <div className="card-header bg-white py-3">
            <div className="row align-items-center g-3">
              <div className="col-md-3">
                <h5 className="mb-0">
                  <i className="bi bi-cash-stack me-2 text-success"></i>
                  Salary Payments
                </h5>
              </div>
              <div className="col-md-9">
                <div className="d-flex gap-2 justify-content-end">
                  <select
                    className="form-select"
                    style={{ maxWidth: '150px' }}
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  >
                    <option value="">All Months</option>
                    {months.map((month) => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                  <select
                    className="form-select"
                    style={{ maxWidth: '120px' }}
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                  >
                    {[2023, 2024, 2025, 2026].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                  <select
                    className="form-select"
                    style={{ maxWidth: '150px' }}
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                  </select>
                  <button
                    className="btn btn-primary"
                    onClick={() => setActiveView('create')}
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Record Payment
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Employee ID</th>
                    <th>Teacher Name</th>
                    <th>Month/Year</th>
                    <th>Basic Salary</th>
                    <th>Allowances</th>
                    <th>Deductions</th>
                    <th>Net Salary</th>
                    <th>Payment Mode</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={10} className="text-center py-4">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredPayments.length > 0 ? (
                    filteredPayments.map((payment) => (
                      <tr key={payment.id}>
                        <td>{payment.employeeId}</td>
                        <td>{payment.teacherName}</td>
                        <td>{payment.month} {payment.year}</td>
                        <td>₹{payment.basicSalary.toLocaleString('en-IN')}</td>
                        <td className="text-success">+₹{payment.allowances.toLocaleString('en-IN')}</td>
                        <td className="text-danger">-₹{payment.deductions.toLocaleString('en-IN')}</td>
                        <td className="fw-bold">₹{payment.netSalary.toLocaleString('en-IN')}</td>
                        <td>
                          <span className="badge bg-info">
                            {payment.paymentMode.replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${
                            payment.status === 'paid' ? 'bg-success' :
                            payment.status === 'pending' ? 'bg-warning' :
                            'bg-danger'
                          }`}>
                            {payment.status.toUpperCase()}
                          </span>
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-outline-primary"
                              title="View Details"
                              onClick={() => {
                                const details = `
Teacher: ${payment.teacherName}
Employee ID: ${payment.employeeId}
Month/Year: ${payment.month} ${payment.year}
Basic Salary: ₹${payment.basicSalary.toLocaleString('en-IN')}
Allowances: ₹${payment.allowances.toLocaleString('en-IN')}
Deductions: ₹${payment.deductions.toLocaleString('en-IN')}
Net Salary: ₹${payment.netSalary.toLocaleString('en-IN')}
Payment Mode: ${payment.paymentMode}
Transaction ID: ${payment.transactionId || 'N/A'}
Status: ${payment.status}
Remarks: ${payment.remarks || 'N/A'}
                                `.trim();
                                alert(details);
                              }}
                            >
                              <i className="bi bi-eye"></i>
                            </button>
                            {payment.status !== 'paid' && (
                              <button
                                className="btn btn-outline-success"
                                title="Mark as Paid"
                                onClick={() => handleUpdateStatus(payment.id, 'paid')}
                              >
                                <i className="bi bi-check-circle"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="text-center text-muted py-5">
                        <i className="bi bi-inbox fs-1 d-block mb-3"></i>
                        No salary payments found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {filteredPayments.length > 0 && (
            <div className="card-footer bg-white">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">
                  Showing {filteredPayments.length} of {payments.length} payments
                </span>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={fetchPayments}
                  disabled={loading}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Refresh
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card shadow-sm">
          <div className="card-header bg-white">
            <h5 className="mb-0">
              <i className="bi bi-plus-circle me-2 text-primary"></i>
              Record Salary Payment
            </h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Select Teacher *</label>
                  <select
                    className="form-select"
                    value={formData.teacherId}
                    onChange={(e) => handleTeacherSelect(e.target.value)}
                    required
                  >
                    <option value="">-- Select Teacher --</option>
                    {teachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.name} ({teacher.employeeId}) - {teacher.designation}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Month *</label>
                  <select
                    className="form-select"
                    value={formData.month}
                    onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                    required
                  >
                    <option value="">-- Select Month --</option>
                    {months.map((month) => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Year *</label>
                  <select
                    className="form-select"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: Number(e.target.value) })}
                    required
                  >
                    {[2023, 2024, 2025, 2026].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Basic Salary *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.basicSalary}
                    onChange={(e) => setFormData({ ...formData, basicSalary: Number(e.target.value) })}
                    required
                    min="0"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Allowances</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.allowances}
                    onChange={(e) => setFormData({ ...formData, allowances: Number(e.target.value) })}
                    min="0"
                    placeholder="HRA, DA, etc."
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Deductions</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.deductions}
                    onChange={(e) => setFormData({ ...formData, deductions: Number(e.target.value) })}
                    min="0"
                    placeholder="TDS, PF, etc."
                  />
                </div>

                <div className="col-md-12">
                  <div className="alert alert-info">
                    <strong>Net Salary: ₹{calculateNetSalary().toLocaleString('en-IN')}</strong>
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Payment Mode *</label>
                  <select
                    className="form-select"
                    value={formData.paymentMode}
                    onChange={(e) => setFormData({ ...formData, paymentMode: e.target.value })}
                    required
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Transaction ID / Reference</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.transactionId}
                    onChange={(e) => setFormData({ ...formData, transactionId: e.target.value })}
                    placeholder="Transaction reference number"
                  />
                </div>

                <div className="col-md-12">
                  <label className="form-label">Remarks</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={formData.remarks}
                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    placeholder="Any additional notes..."
                  />
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end mt-4">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setActiveView('list')}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Record Payment
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryPayment;
